import { useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { OwnershipTree } from '../OwnershipTree';
import paynovusLogo from '@/assets/paynovus-logo-horizontal-tagline.svg';

const STORAGE_KEY = 'paynovus_onboarding_progress';

export function SuccessStep() {
  const { data, setCurrentStep } = useOnboarding();

  // Clear localStorage when reaching success
  useEffect(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStep('welcome');
  };

  const isCorporate = data.accountType === 'corporate';
  const company = data.corporateCompany;

  return (
    <div className="min-h-screen-safe hero-dark flex flex-col items-center justify-center p-4 sm:p-6 text-white animate-fade-in-up safe-area-inset-top safe-area-inset-bottom">
      <div className="text-center max-w-md w-full px-2">
        <img src={paynovusLogo} alt="PayNovus" className="h-10 sm:h-14 w-auto mx-auto mb-5 sm:mb-6" />
        <div className="relative inline-block mb-5 sm:mb-8">
          <div className="w-18 h-18 sm:w-24 sm:h-24 rounded-full bg-success/20 flex items-center justify-center" style={{ width: '72px', height: '72px' }}>
            <CheckCircle className="w-9 h-9 sm:w-12 sm:h-12 text-success" />
          </div>
          <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2">
            <Sparkles className="w-5 h-5 sm:w-8 sm:h-8 text-accent animate-pulse" />
          </div>
        </div>

        <h1 className="text-xl sm:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
          {isCorporate ? `Welcome aboard, ${company?.name}!` : `Welcome aboard, ${data.firstName}!`}
        </h1>
        
        <p className="text-white/70 text-sm sm:text-lg mb-5 sm:mb-8">
          Your account is all set up and ready to go. You're now part of the future of banking.
        </p>

        {/* Corporate Ownership Tree */}
        {isCorporate && company && (
          <div className="mb-5 sm:mb-6">
            <OwnershipTree
              companyName={company.name}
              registrationNumber={company.registrationNumber}
              directors={company.directors}
              shareholders={company.shareholders}
            />
          </div>
        )}

        {/* Individual Account Summary */}
        {!isCorporate && (
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-6 mb-5 sm:mb-8 text-left">
            <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-lg">Account Summary</h3>
            <div className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm">
              <div className="flex justify-between items-center">
                <span className="text-white/60">Name</span>
                <span className="font-medium">{data.firstName} {data.lastName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/60">Email</span>
                <span className="font-medium truncate ml-4 max-w-[180px]">{data.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/60">Phone</span>
                <span className="font-medium">{data.countryCode} {data.phoneNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/60">Verification</span>
                <span className="text-success font-medium">Complete ✓</span>
              </div>
            </div>
          </div>
        )}

        <button 
          onClick={() => window.location.href = '/app/login'}
          className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-full bg-accent hover:bg-accent/90 active:bg-accent/80 text-accent-foreground flex items-center justify-center gap-2 mb-3 sm:mb-4 transition-all touch-manipulation"
          style={{ minHeight: '48px' }}
        >
          Explore your Account
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={handleStartOver}
          className="text-white/60 hover:text-white active:text-white/40 transition-colors text-xs sm:text-sm py-2 touch-manipulation"
          style={{ minHeight: '44px' }}
        >
          Start over (demo)
        </button>
      </div>
    </div>
  );
}
